import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import { LogOut, ShoppingBag, Heart, MapPin } from "lucide-react";
import { toast } from "sonner";

export default function Account() {
    const { user, authLoading, logout } = useApp();
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);

    useEffect(() => {
        if (authLoading) return;
        if (!user) { navigate("/login"); return; }
        api.get("/orders/me").then(r => setOrders(r.data)).catch(() => {});
    }, [user, authLoading, navigate]);

    if (authLoading || !user) return <div className="px-6 py-20 text-[color:var(--ms-muted)] text-sm">Caricamento…</div>;

    const onLogout = async () => {
        await logout();
        toast.success("Disconnesso");
        navigate("/");
    };

    return (
        <div className="px-6 md:px-12 lg:px-24 py-16" data-testid="account-page">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
                <div>
                    <div className="overline mb-3">Il tuo account</div>
                    <h1 className="font-display text-5xl tracking-tight text-[color:var(--ms-charcoal)]">Ciao, {user.name.split(" ")[0]}.</h1>
                    <p className="text-[color:var(--ms-muted)] text-sm mt-2">{user.email}</p>
                </div>
                <button onClick={onLogout} className="border border-[color:var(--ms-border)] text-[color:var(--ms-charcoal)] px-5 py-3 text-xs tracking-[0.2em] uppercase flex items-center gap-2 hover:border-[color:var(--ms-charcoal)] transition-colors" data-testid="account-logout">
                    <LogOut size={14} /> Esci
                </button>
            </div>

            <div className="grid lg:grid-cols-[1fr_320px] gap-10">
                <div>
                    <div className="overline mb-4 flex items-center gap-2"><ShoppingBag size={14} /> Ordini</div>
                    {orders.length === 0 ? (
                        <div className="panel p-10 text-center text-[color:var(--ms-muted)] text-sm" data-testid="account-no-orders">
                            Nessun ordine ancora. <Link to="/shop" className="text-[color:var(--ms-sage-dark)] link-underline">Scopri la collezione</Link>
                        </div>
                    ) : (
                        <ul className="divide-y divide-[color:var(--ms-border)] panel">
                            {orders.map(o => (
                                <li key={o.id} className="p-5" data-testid={`order-row-${o.id}`}>
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <div className="overline">{new Date(o.created_at).toLocaleDateString("it-IT")}</div>
                                            <div className="text-sm text-[color:var(--ms-charcoal)] font-mono">{o.id.slice(0, 8)}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-[color:var(--ms-charcoal)] font-medium">€{o.total.toFixed(2)}</div>
                                            <span className={`overline ${o.payment_status === 'paid' ? "text-[color:var(--ms-sage-dark)]" : "text-[color:var(--ms-muted)]"}`}>{o.payment_status === "paid" ? "Pagato" : o.payment_status}</span>
                                        </div>
                                    </div>
                                    <div className="text-xs text-[color:var(--ms-muted)]">{o.items.length} {o.items.length === 1 ? "capo" : "capi"} · {o.items.map(i => i.title).slice(0,2).join(", ")}{o.items.length>2 ? "…" : ""}</div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <aside className="space-y-6">
                    <div className="panel p-6">
                        <div className="overline mb-3 flex items-center gap-2"><Heart size={14} /> Preferiti</div>
                        <div className="text-sm text-[color:var(--ms-charcoal)]">{user.wishlist?.length || 0} capi salvati</div>
                        <Link to="/wishlist" className="overline mt-3 inline-block link-underline text-[color:var(--ms-sage-dark)]" data-testid="account-wishlist-link">VEDI PREFERITI</Link>
                    </div>
                    <div className="panel p-6">
                        <div className="overline mb-3 flex items-center gap-2"><MapPin size={14} /> Indirizzi</div>
                        <p className="text-sm text-[color:var(--ms-muted)]">Gli indirizzi inseriti al checkout verranno salvati automaticamente.</p>
                    </div>
                </aside>
            </div>
        </div>
    );
}
